<template>
  <div>
    <div class="row mb-30">
      <div class="col-12" style="display: flex; justify-content: space-between">
        <account-type-tabs selected="counselors" />
        <button class="btn btn-primary" @click="onCreate">
          Invite Counselor
        </button>
      </div>
    </div>

    <div class="row">
      <div class="col-12">
        <div class="dashboard-card">
          <counselor-filters
            v-model="filters"
            @input="handleCounselorFiltersInput"
            @reset="handleCounselorFiltersReset"
          />
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-12">
        <counselor-table-tabs
          :selected="filters.columnSet"
          :on-select="onColumnSetSelected"
        />

        <div class="dashboard-card">
          <loader v-if="!counselors" style="width: 100%" />

          <counselor-table
            v-else
            :counselors="counselors"
            :column-set="filters.columnSet"
            :sorted-column="sortedColumn"
            :sorted-asc="sortedAsc"
            :on-sort="onSort"
            :send-message="sendMessage"
            :view-counselor="viewCounselor"
            :view-availability="viewAvailability"
            :can-load-more="nextPageToken != null"
            :load-more-counselors="loadCounselors"
            @impersonate="(id) => $emit('impersonate', id)"
          />
        </div>
      </div>
    </div>

    <send-message-modal ref="sendMessageModal" />

    <view-counselor-modal
      ref="viewCounselorModal"
      :reload-counselors="loadCounselors"
    />

    <invite-counselor-modal ref="inviteCounselorModal" @created="onCreated" />
  </div>
</template>

<script>
import AdminClient from '@/api/AdminClient';
import UserClient from '@/api/UserClient';
import PayrollClient from '@/api/CounselorManagement/PayrollClient';
import Loader from '@/components/Loader.vue';
import SendMessageModal from '@/components/modals/SendMessageModal.vue';
import AccountTypeTabs from './components/AccountTypeTabs.vue';
import CounselorFilters from './components/CounselorFilters.vue';
import CounselorTable from './components/CounselorTable.vue';
import CounselorTableTabs from './components/CounselorTableTabs.vue';
import ViewCounselorModal from './components/ViewCounselorModal.vue';
import InviteCounselorModal from './components/InviteCounselorModal.vue'
import moment from 'moment'

export default {
  name: 'SuperAdminCounselors',

  components: {
    Loader,
    SendMessageModal,
    AccountTypeTabs,
    CounselorFilters,
    CounselorTable,
    CounselorTableTabs,
    ViewCounselorModal,
    InviteCounselorModal
  },

  props: {
    search: {
      type: String,
      required: true
    },
    csvBus: {
      type: Object,
      required: true
    }
  },

  data() {
    const q = new URLSearchParams(window.location.search);

    return {
      adminClient: new AdminClient(this.$http),
      userClient: new UserClient(this.$http),
      payrollClient: new PayrollClient(this.$http),
      timeout: null,

      filters: {
        active: q.has('active') ? q.get('active') : '',
        verified: q.has('verified') ? q.get('verified') : '',
        hasAvailability: q.has('hasAvailability') ? q.get('hasAvailability') : '',
        stateId: q.has('stateId') ? q.get('stateId') : '',
        languageId: q.has('languageId') ? q.get('languageId') : '',
        columnSet: q.has('columnSet') ? q.get('columnSet') : 'overview'
      },

      sortedColumn: 'date_joined',
      sortedAsc: false,
      sortedBy: '',
      counselors: [],
      nextPageToken: null,

    };
  },

  watch: {
    search() {
      if (this.timeout) {
        clearTimeout(this.timeout);
        this.timeout = null;
      }
      this.timeout = setTimeout(() => {
        this.nextPageToken = this.sortedBy;
        this.counselors = [];
        this.loadCounselors()
      }, 300);
    }
  },

  mounted() {
    this.csvBus.$on('generate', this.generateCsv);
    this.loadCounselors();
  },

  destroyed() {
    this.csvBus.$off('generate', this.generateCsv);
  },

  methods: {
    async loadCounselors() {
      try {
        const response = await this.adminClient.getAllCounselorsForAdminReport(
          this.parseSearchValue(this.search),
          this.parseBooleanValue(this.filters.active),
          this.parseBooleanValue(this.filters.verified),
          this.parseBooleanValue(this.filters.hasAvailability),
          this.parseIntegerValue(this.filters.stateId),
          this.parseIntegerValue(this.filters.languageId),
          this.nextPageToken
        );

        this.nextPageToken = response.nextPageToken;
        const results = response.counselors.map(counselor => ({
          ...counselor,
          id: counselor.counselorId,
          dateJoined: counselor.dateJoined ? new Date(counselor.dateJoined) : null,
          lastLogin: counselor.lastLogin ? new Date(counselor.lastLogin) : null,
        })) || [];

        results.forEach(result => this.counselors.push(result));
      } catch (ex) {
        console.error(ex);
      }
    },

    handleCounselorFiltersInput() {
      this.$router.push({
        name: 'accounts-counselors',
        query: {
          active:
            this.filters.active === '' ? undefined : this.filters.active,
          verified:
            this.filters.verified === '' ? undefined : this.filters.verified,
          hasAvailability:
            this.filters.hasAvailability === '' ? undefined : this.filters.hasAvailability,
          stateId:
            this.filters.stateId === '' ? undefined : this.filters.stateId,
          language:
            this.filters.languageId === '' ? undefined : this.filters.languageId,
          columnSet:
            this.filters.columnSet === 'overview'
              ? undefined
              : this.filters.columnSet
        }
      });
      this.nextPageToken = this.sortedBy;
      this.counselors = [];
      this.loadCounselors();
    },

    handleCounselorFiltersReset() {
      this.filters.active = '';
      this.filters.verified = '';
      this.filters.hasAvailability = '';
      this.filters.stateId = '';
      this.filters.languageId = '';
      this.filters.columnSet = 'overview';

      this.$router.push({
        name: 'accounts-counselors',
        query: {
          active: undefined,
          verified: undefined,
          hasAvailability: undefined,
          stateId: undefined,
          languageId: undefined,
          columnSet: undefined,
        }
      });
      this.nextPageToken = null;
      this.counselors = [];
      this.loadCounselors();
    },

    onColumnSetSelected(columnSet) {
      this.filters.columnSet = columnSet;

      this.$router.push({
        name: 'accounts-counselors',
        query: {
          active:
            this.filters.active === '' ? undefined : this.filters.active,
          verified:
            this.filters.verified === '' ? undefined : this.filters.verified,
          hasAvailability:
            this.filters.hasAvailability === '' ? undefined : this.filters.hasAvailability,
          stateId:
            this.filters.stateId === '' ? undefined : this.filters.stateId,
          languageId:
            this.filters.languageId === '' ? undefined : this.filters.languageId,
          columnSet:
            this.filters.columnSet === 'overview'
              ? undefined
              : this.filters.columnSet
        }
      });
    },

    onSort(columnsSortTokens, id) {
      if (columnsSortTokens.includes(null)) return;

      if (id === this.sortedColumn) {
        this.nextPageToken = columnsSortTokens.find(x => x !== this.sortedBy);
      } else {
        this.nextPageToken = columnsSortTokens[1];
      }

      this.sortedBy = this.nextPageToken;
      this.sortedAsc = this.sortedBy.includes('Asc');
      this.sortedColumn = id;

      this.counselors = [];
      this.loadCounselors();
    },

    sendMessage(filters) {
      this.$refs.sendMessageModal.show(filters.counselor.userId);
    },
    async viewCounselor(counselor) {
      try {
        counselor.payroll = await this.payrollClient.getPayroll(counselor.id);
        const profile = await this.userClient.getCounselorProfile(counselor.id);

        profile.adminModel = counselor;
        profile.payroll = counselor.payroll;
        this.$refs.viewCounselorModal.show(profile);
      } catch (e) {
        this.$refs.viewCounselorModal.show(counselor);
      }
    },
    async viewAvailability(counselor, mode = 'availability') {
      this.$router.push({ name: 'availability', query: { 'id': counselor.id } })
    },
    async generateCsv() {
      try {
        let pageToken = this.sortedBy !== undefined ? this.sortedBy : 'GetCounselorAccountsByDateJoinedDesc';
   
        const data = [];
        while (pageToken != null) {
          const response = await this.getCounselorsForCSV(pageToken);
          const results = response.counselors.map(counselor => ({
            CounselorId: counselor.counselorId,
            FirstName: counselor.firstName,
            LastName: counselor.lastName,
            Email: counselor.email,
            IsActive: counselor.isActive ? 'Yes' : 'No',
            IsVerified: counselor.verified ? 'Yes' : 'No',
            DateJoined: moment(counselor.dateJoined).format('YYYY-MM-DD'),
            LastLogin: counselor.lastLogin ? moment(counselor.lastLogin).format('YYYY-MM-DD') : ' - ',
            HasSetWorkingHours: counselor.hasSetWorkingHours ? 'Yes' : 'No',
            CalendarInitialized: counselor.calendarInitialized ? 'Yes' : 'No',
            RegisteredStates: counselor.registeredStates.length > 0 ? counselor.registeredStates.join(' / ') : ' - ',
            ApprovedRequests: counselor.requests.approved,
            PendingRequests: counselor.requests.pending,
            ExpiredRequests: counselor.requests.expired,
            DeclinedRequests: counselor.requests.declined,
            CanceledRequests: counselor.requests.canceled,
            CanceledSessions: counselor.sessions.canceled,
            CanceledSessionsByCounselor: counselor.sessions.canceledByCounselor,
          }));

          pageToken = response.nextPageToken;
          results.forEach(result => data.push(result));
        }

        const content = this.convertToCsv(data);
        this.csvBus.$emit('success', { content, file: 'counselors.csv' });
      } catch (e) {
        this.csvBus.$emit('error', e);
      }
    },
    convertToCsv(data) {
      const header = `${Object.keys(data[0]).join(',')}\n`;
      const csvRows = data.map((obj) =>
        Object.values(obj).map((value) => JSON.stringify(value)).join(',')
      );

      return header + csvRows.join('\n');
    },
    async getCounselorsForCSV(pageToken) {
      return this.adminClient.getAllCounselorsForAdminReport(
          this.parseSearchValue(this.search),
          this.parseBooleanValue(this.filters.active),
          this.parseBooleanValue(this.filters.verified),
          this.parseBooleanValue(this.filters.hasAvailability),
          this.parseIntegerValue(this.filters.stateId),
          this.parseIntegerValue(this.filters.languageId),
          pageToken,
          50
        );
    },
    onCreate() {
      this.$refs.inviteCounselorModal.show();
    },
    onCreated() {
      this.loadCounselors();
    },
    parseBooleanValue(value) {
      return value !== '' ? JSON.parse(value) : null;
    },
    parseIntegerValue(value) {
      return value !== '' ? parseInt(value, 10) : null;
    },
    parseSearchValue(value) {
      return value !== '' ? value.split(/\s/).filter(split => split).map(split => split.trim()) : null;
    }
  }
};
</script>
